import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CancelModal, SuccessModal } from "./modals";
import SubPopup from "../../../components/NewJob/SubPopup/popup";
import { errorHandler } from "../../../utils";
import {
  clientActiveSubscription,
  clientSubscribeToPlan,
  clientSubscriptionPlans,
  companyProfile,
  findClientSubscription,
  getClientCurrentSubscription,
  postSubscription,
  subscriptionHistory,
} from "../../../services/clientCalls";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import { useLocation, useNavigate } from "react-router";
import { DEFAULT_CLIENT_SUBSCRIPTION } from "../../../constants";
import Breadcrumb from "../../../components/UI/Breadcrumb/Breadcrumb";
import styles from "./subscriptionPayment.module.css";
import { Toast } from "../../../components/Toast/Toast";
import { initialBillingInfo } from "./components/data";
import PaymentHistoryCard from "./components/PaymentHistory";
import PolicyStrip from "./components/PolicyStrip";
import TopUpPacksSection from "./components/TopUpPacksSection";
import PlanHero from "./components/PlanHero";
import PricingSection from "./components/PricingSection";
import CurrentPlanCard from "./components/CurrentPlanCard";
import BillingInfoCard from "./components/BillingInfoCard";
import NeedHelpCard from "./components/NeedHelpCard";
import CancelPlanLink from "./components/CancelPlanLink";
import { useUserLocation } from "../../../hooks/useUserLocation";
import AddTeamModal from "../../../components/TeamManagement/AddTeamModal";
import { UserContext } from "../../../context";
import { verifySubscriptionTransaction } from "../../../services/attributionCalls";
import { readPendingPurchase, rememberPendingPurchase, trackPurchase } from "../../../utils/analyticsEvents";

const SubscriptionPayment = () => {
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(null);
  const [subscribeIsLoading, setSubscribeIsLoading] = useState(false);
  const [subscriptionHistoryArr, setSubscriptionHistoryArr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [annualPlans, setAnnualPlans] = useState([]);
  const [topUpPlans, setTopUpPlans] = useState([]);
  const [currency, setCurrency] = useState("NGN");
  const [selectedPlanType, setSelectedPlanType] = useState(null); // "annual" | "topup"
  const [billingInfo, setBillingInfo] = useState(initialBillingInfo);
  const [historyPage, setHistoryPage] = useState(1);
  const [toast, setToast] = useState({ message: "", visible: false });
  const toastTimer = useRef(null);
  const plansSectionRef = useRef(null);
  const packsSectionRef = useRef(null);
  const [selectedPlanName, setSelectedPlanName] = useState(
    DEFAULT_CLIENT_SUBSCRIPTION,
  );
  const [page, setPage] = useState(1);
  const location = useLocation();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);

  const { isNigerian } = useUserLocation();

  // Sync display currency with detected location once resolved
  useEffect(() => {
    if (isNigerian !== null) {
      setCurrency(isNigerian ? "NGN" : "USD");
    }
  }, [isNigerian]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reference = params.get("reference") || params.get("trxref");
    if (!reference) return undefined;
    let cancelled = false;
    verifySubscriptionTransaction(reference)
      .then((res) => {
        if (cancelled) return;
        const paystack = res?.data?.data?.paystack;
        const pending = readPendingPurchase();
        const amount = Number(paystack?.amount);
        trackPurchase({
          transactionId: reference,
          value: Number.isFinite(amount) ? amount / 100 : pending?.value,
          currency: paystack?.currency || pending?.currency,
          itemName: pending?.item_name,
          itemCategory: pending?.item_category,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const fetchClientActiveSubscription = async () => {
    try {
      const res = await getClientCurrentSubscription();
      const sub = res?.data?.data?.data;
      setActiveSubscription(sub ?? {});
    } catch (error) {
      setActiveSubscription({});
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleCancelSubscription = () => {
    setSelectedPlan(null);
    setShowCancelModal(false);
  };

  const fetchSubscription = async (page) => {
    setLoading(true);
    try {
      const res = await subscriptionHistory(page);

      if (res?.data?.data?.records?.length > 0) {
        setSubscriptionHistoryArr(res?.data?.data?.records);
      }

      if (page !== res?.data?.data?.counts) {
        setSubscriptionData(res?.data?.data);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoading(false);
    }
  };

  const handleFindClientSubscription = async () => {
    try {
      const payload = {
        name: selectedPlanName,
      };
      if (selectedPlanName) {
        const res = await findClientSubscription(payload);
        const subscriptionId = res?.data?.data?.subscription?._id;
        if (subscriptionId) {
          return subscriptionId;
        }
      } else {
        errorHandler("Please select a plan", "Error");
        return null;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  // ── Subscribe ─── //
  const handleSubscription = useCallback(async () => {
    if (!selectedPlan) return;
    setSubscribeIsLoading(true);
    try {
      const payload = {
        planId: selectedPlan?.id,
        planType: selectedPlanType,
        currency: currency,
      };

      const res = await clientSubscribeToPlan(payload);
      rememberPendingPurchase({
        item_name: selectedPlan?.name || "credits",
        item_category: selectedPlanType === "annual" ? "annual" : "topup",
        value: selectedPlan?.price,
        currency,
      });

      // Paystack authorization_url — check common nesting patterns
      const authUrl =
        res?.data?.data?.authorization_url ??
        res?.data?.data?.data?.authorization_url ??
        res?.data?.data?.data?.data?.authorization_url;

      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeIsLoading(false);
    }
  }, [selectedPlan, selectedPlanType, currency]);

  // ── Fetch plans ─── //
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await clientSubscriptionPlans();
      if (res?.data?.data?.data) {
        setAnnualPlans(res?.data?.data?.data?.annualPlans ?? []);
        setTopUpPlans(res?.data?.data?.data?.topUpPlans ?? []);
      }
    } catch (error) {
      error.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoading(false);
    }
  };

  const getProfile = async () => {
    try {
      const res = await companyProfile();
      if (res?.data?.data?.client) {
        setProfileData(res?.data?.data?.client);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  useEffect(() => {
    fetchSubscription();
    fetchClientActiveSubscription();
    fetchPlans();
    getProfile();
  }, []);

  function showToast(message) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, visible: true });
    toastTimer.current = setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 3000);
  }

  function scrollToPacks() {
    packsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function scrollToPlans() {
    plansSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function handleSaveBilling(newInfo) {
    setBillingInfo(newInfo);
    showToast("Billing info updated");
  }

  const handleSelectPlan = (plan, planType) => {
    const payCurrency = isNigerian ? "NGN" : "USD";
    setSelectedPlan(plan);
    setSelectedPlanType(planType);
    setCurrency(payCurrency);
    setShowOrderSummary({
      kind: plan?.planKind,
      name: plan?.name,
      credits: plan.pricePerCredit,
      price: plan?.price,
      perCredit: plan?.pricePerCredit,
      cpm: plan?.pricePerCredit,
      currency: plan?.currency,
    });
  };

  const handleCloseModal = useCallback(() => {
    setShowOrderSummary(null);
    setSelectedPlan(null);
    setSelectedPlanType(null);
    setCurrency("NGN");
  }, []);

  return (
    <>
      {loading && <FullScreenLoader />}
      <Breadcrumb
        crumbs={[
          { label: "Dashboard", href: "/client-activity-summary" },
          { label: "Subscription & Payment" },
        ]}
        title="Subscription & Payment"
      />
      <p
        className={styles.pageSubtitle}
        style={{ marginTop: -16, marginBottom: 24 }}
      >
        Manage your credits, plan and invoices
      </p>

      <div className={styles.fullWidthSection}>
        <PlanHero
          plan={activeSubscription}
          onScrollToPacks={scrollToPacks}
          onScrollToPlans={scrollToPlans}
        />

        <PricingSection
          ref={plansSectionRef}
          apiData={annualPlans}
          activeSubscription={activeSubscription}
          currency={currency}
          onCta={(plan) => handleSelectPlan(plan, "annual")}
        />

        <TopUpPacksSection
          ref={packsSectionRef}
          topUpPlans={topUpPlans}
          activeSubscription={activeSubscription}
          currency={currency}
          onCta={(pack) => handleSelectPlan(pack, "topup")}
        />

        <PolicyStrip />
      </div>

      <div className={styles.subGrid}>
        <div className={styles.subMain}>
          <PaymentHistoryCard
            billingData={subscriptionHistoryArr}
            fetchSubscription={fetchSubscription}
            subscriptionData={subscriptionData}
            setPage={setPage}
            onDownloadAll={() => showToast("Downloading all invoices…")}
            onDownloadRow={() => showToast("Downloading invoice…")}
          />
        </div>

        <div className={styles.subSide}>
          {activeSubscription?.planName && (
            <CurrentPlanCard plan={activeSubscription} />
          )}
          <BillingInfoCard info={profileData} onSave={handleSaveBilling} />
          <NeedHelpCard
            onContactSupport={() => {
              navigate("/client-report");
            }}
          />
          <CancelPlanLink
            onClick={() =>
              showToast(
                "Your plan runs its full term — unused credits stay frozen, never lost. Contact support to proceed.",
              )
            }
          />
        </div>
      </div>

      <Toast message={toast.message} visible={toast.visible} />

      {/* Modals */}
      {showCancelModal && (
        <CancelModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelSubscription}
        />
      )}

      {showSuccessModal && (
        <SuccessModal onClose={() => setShowSuccessModal(false)} />
      )}

      {/* Order Summary Modal */}
      <SubPopup
        order={showOrderSummary}
        onClose={handleCloseModal}
        defaultEmail={profileData?.email}
        loading={subscribeIsLoading}
        handlePay={handleSubscription}
      />
    </>
  );
};

export default SubscriptionPayment;
